import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { useApp } from "../contexts/AppContext";
import api from "../lib/api";
import t from "../lib/i18n";
import { Plus, Users, Clock, Flower2, Activity, Moon, Heart, Sparkles, Mail, Coffee } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bn-card p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-semibold tracking-wide uppercase" style={{ color: "var(--bn-text-secondary)" }}>
        {label}
      </span>
      <div
        className="w-10 h-10 rounded-2xl flex items-center justify-center"
        style={{ background: accent || "var(--bn-surface)" }}
      >
        <Icon size={18} />
      </div>
    </div>
    <div className="display text-3xl font-semibold">{value}</div>
  </div>
);

export default function ParentDashboard() {
  const { parent, language, settings, updateSettings } = useApp();
  const [children, setChildren] = useState([]);
  const [progress, setProgress] = useState({ totalActivities: 0, totalMinutes: 0, totalFlowers: 0 });
  const [recentCheckins, setRecentCheckins] = useState([]);
  const [rhythms, setRhythms] = useState(null);

  // Soft weekly Reflection Pause invite (opt-in, dismissable, never repeats within 7 days)
  const showPauseInvite = (() => {
    if (settings?.pause_enabled === false) return false;
    const last = settings?.last_pause_offered_at;
    if (!last) return true;
    const daysSince = (Date.now() - new Date(last).getTime()) / (1000 * 60 * 60 * 24);
    return daysSince >= 7;
  })();

  const dismissPauseInvite = async () => {
    await updateSettings({ last_pause_offered_at: new Date().toISOString() });
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/children");
        setChildren(data);
        let acts = 0, mins = 0, flowers = 0, checkins = [];
        for (const c of data) {
          flowers += c.flowers_grown || 0;
          acts += c.activities_completed || 0;
          try {
            const { data: prog } = await api.get(`/progress/${c.id}`);
            mins += prog.total_minutes || 0;
          } catch {}
          try {
            const { data: ch } = await api.get(`/checkins/${c.id}`);
            checkins = checkins.concat(ch.slice(0, 3).map((x) => ({ ...x, childName: c.name })));
          } catch {}
        }
        setProgress({ totalActivities: acts, totalMinutes: mins, totalFlowers: flowers });
        setRecentCheckins(checkins.slice(0, 5));
        // Pull rhythms for the first child as a soft surface
        if (data.length > 0) {
          try {
            const { data: r } = await api.get(`/rhythms/${data[0].id}`);
            setRhythms({ ...r, child_id: data[0].id });
          } catch {}
        }
      } catch {}
    })();
  }, []);

  return (
    <Layout>
      <div className="bn-fade">
        <div className="flex items-baseline justify-between flex-wrap gap-4 mb-2">
          <h1 className="display text-4xl md:text-5xl font-semibold" data-testid="dashboard-title">
            {t(language, "welcome")}, {parent?.name?.split(" ")[0]}
          </h1>
          <Link
            to="/profiles"
            className="bn-pill bn-primary-bg flex items-center gap-2"
            data-testid="dashboard-add-child"
          >
            <Plus size={18} /> {t(language, "addChild")}
          </Link>
        </div>
        <p className="text-lg mb-10" style={{ color: "var(--bn-text-secondary)" }}>
          {t(language, "parentDashboard")} · {t(language, "insights")}
        </p>

        {showPauseInvite && (
          <div
            className="bn-card p-6 md:p-7 mb-8 flex items-start gap-4"
            style={{ background: "linear-gradient(135deg, rgba(244,199,83,0.18) 0%, rgba(165,185,160,0.15) 100%)" }}
            data-testid="pause-invite"
          >
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 bn-breathe"
              style={{ background: "var(--bn-surface-alt)" }}
            >
              <Coffee size={20} />
            </div>
            <div className="flex-1">
              <div className="text-xs font-semibold tracking-wide uppercase mb-1" style={{ color: "var(--bn-text-secondary)" }}>
                {t(language, "pauseInviteTag")}
              </div>
              <div className="display text-lg md:text-xl font-semibold leading-relaxed mb-3">
                {t(language, "pauseInviteBody")}
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to="/pause" className="bn-pill bn-primary-bg text-sm" data-testid="pause-invite-begin">
                  {t(language, "pauseInviteBegin")}
                </Link>
                <button
                  onClick={dismissPauseInvite}
                  className="bn-pill text-sm"
                  style={{ background: "var(--bn-surface)" }}
                  data-testid="pause-invite-later"
                >
                  {t(language, "pauseInviteLater")}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-10">
          <StatCard icon={Users} label={t(language, "dashChildren")} value={children.length} accent="var(--bn-surface)" />
          <StatCard icon={Activity} label={t(language, "dashActivities")} value={progress.totalActivities} accent="#F2EBE1" />
          <StatCard icon={Clock} label={t(language, "dashMinutes")} value={progress.totalMinutes} accent="#F2EBE1" />
          <StatCard icon={Flower2} label={t(language, "dashFlowers")} value={progress.totalFlowers} accent="#F2EBE1" />
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          <div className="lg:col-span-2 bn-card p-7">
            <h2 className="display text-2xl font-semibold mb-5">{t(language, "childProfiles")}</h2>
            {children.length === 0 ? (
              <div className="text-center py-10">
                <p className="mb-5" style={{ color: "var(--bn-text-secondary)" }}>
                  {t(language, "dashNoChildren")}
                </p>
                <Link to="/profiles" className="bn-pill bn-primary-bg" data-testid="dashboard-empty-add">
                  {t(language, "addChild")}
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {children.map((c) => (
                  <Link
                    key={c.id}
                    to={`/child/${c.id}`}
                    className="rounded-2xl p-5 transition-all duration-300 hover:translate-y-[-2px]"
                    style={{ background: "var(--bn-surface)", border: "1px solid var(--bn-border)" }}
                    data-testid={`child-card-${c.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl"
                        style={{ background: "var(--bn-primary)" }}
                      >
                        🐝
                      </div>
                      <div>
                        <div className="display text-xl font-semibold">{c.name}</div>
                        <div className="text-sm" style={{ color: "var(--bn-text-secondary)" }}>
                          {t(language, "dashAgesPrefix")} {c.age_range} · {c.flowers_grown} {t(language, "dashFlowersSuffix")}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="bn-card p-7">
            <h2 className="display text-2xl font-semibold mb-5 flex items-center gap-2">
              <Heart size={20} aria-hidden="true" /> {t(language, "dashRecentCheckins")}
            </h2>
            {recentCheckins.length === 0 ? (
              <p style={{ color: "var(--bn-text-secondary)" }}>{t(language, "dashNoCheckins")}</p>
            ) : (
              <ul className="space-y-3">
                {recentCheckins.map((c) => (
                  <li key={c.id} className="flex items-center gap-3" data-testid={`checkin-${c.id}`}>
                    <span className="text-2xl">{feelEmoji(c.feeling)}</span>
                    <div>
                      <div className="font-semibold">{c.childName} · {c.feeling}</div>
                      <div className="text-xs" style={{ color: "var(--bn-text-secondary)" }}>
                        {new Date(c.created_at).toLocaleString()}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {rhythms && (
            <Link
              to={`/child/${rhythms.child_id}/rhythms`}
              className="bn-card p-7 hover:translate-y-[-2px] transition-transform duration-300 md:col-span-2"
              style={{ background: "linear-gradient(135deg, var(--bn-surface) 0%, var(--bn-surface-alt) 100%)" }}
              data-testid="dashboard-rhythms-card"
            >
              <div className="flex items-center gap-2 mb-3 text-sm font-semibold tracking-wide uppercase" style={{ color: "var(--bn-text-secondary)" }}>
                <Sparkles size={14} aria-hidden="true" /> {t(language, "dashRhythmsLabel")}
              </div>
              <div className="display text-xl md:text-2xl font-semibold mb-2 leading-relaxed">
                {rhythms.reflection}
              </div>
              <div className="text-sm" style={{ color: "var(--bn-text-secondary)" }}>
                {rhythms.calming_moments_this_week} {t(language, "dashRhythmsCount")} ·{" "}
                <span className="font-semibold underline-offset-4 hover:underline" style={{ color: "var(--bn-accent-terracotta)" }}>
                  {t(language, "dashRhythmsOpen")}
                </span>
              </div>
            </Link>
          )}
          <Link to="/settings" className="bn-card p-7 hover:translate-y-[-2px] transition-transform duration-300" data-testid="dashboard-settings-card">
            <Moon size={24} className="mb-3" aria-hidden="true" />
            <h3 className="display text-xl font-semibold mb-1">{t(language, "dashSensoryTitle")}</h3>
            <p style={{ color: "var(--bn-text-secondary)" }}>{t(language, "dashSensoryBody")}</p>
          </Link>
          <Link to="/letters" className="bn-card p-7 hover:translate-y-[-2px] transition-transform duration-300" data-testid="dashboard-letters-card">
            <Mail size={24} className="mb-3" style={{ color: "var(--bn-accent-terracotta)" }} aria-hidden="true" />
            <h3 className="display text-xl font-semibold mb-1">{t(language, "dashLettersTitle")}</h3>
            <p style={{ color: "var(--bn-text-secondary)" }}>{t(language, "dashLettersBody")}</p>
          </Link>
          <Link to="/membership" className="bn-card p-7 hover:translate-y-[-2px] transition-transform duration-300" data-testid="dashboard-membership-card">
            <Flower2 size={24} className="mb-3" aria-hidden="true" />
            <h3 className="display text-xl font-semibold mb-1">{t(language, "membership")}</h3>
            <p style={{ color: "var(--bn-text-secondary)" }}>{t(language, "dashMembershipBody")}</p>
          </Link>
        </div>
      </div>
    </Layout>
  );
}

function feelEmoji(f) {
  return { happy: "🌼", calm: "🌿", tired: "🌙", sad: "💧", frustrated: "🌧️", excited: "🌻" }[f] || "🐝";
}
